import { useEffect, useState } from 'react';
import { LIVE_TV_QUALITY_MODE, LIVE_TV_TRUSTED_CATALOG_MODE } from '../constants/liveTv.constants';
import {
  getLiveTvChannels,
  hasPlayableSource,
  isBrowserPlayableStreamUrl,
  isTrustedLiveTvChannel,
} from '../services/liveTv.service';
import { LiveTvChannelWithStream } from '../types/liveTv.types';

interface UseLiveTvChannelsResult {
  channels: LiveTvChannelWithStream[];
  loading: boolean;
  error: string | null;
}

export function useLiveTvChannels(): UseLiveTvChannelsResult {
  const [channels, setChannels] = useState<LiveTvChannelWithStream[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError(null);

        const data = await getLiveTvChannels();

        if (cancelled) return;

        const filtered = data.filter((channel) => {
          if (!channel.enabled) return false;

          // forceEnable permite enseñar canales aunque la fuente falle para revisar el error en pantalla.
          if (channel.forceEnable) return true;

          // No mostramos canales no forzados sin stream, YouTube o embed reproducible.
          if (!hasPlayableSource(channel)) return false;

          if (LIVE_TV_TRUSTED_CATALOG_MODE && !isTrustedLiveTvChannel(channel)) {
            return false;
          }

          // browserVerified tiene prioridad.
          if (channel.browserVerified) return true;

          if (LIVE_TV_QUALITY_MODE) {
            if (isBrowserPlayableStreamUrl(channel.stream?.url)) {
              return Boolean(channel.browserCompatible);
            }

            // Si no hay stream compatible pero sí YouTube/embed válido, lo dejamos pasar.
            return hasPlayableSource(channel);
          }

          return true;
        });

        setChannels(filtered);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  return { channels, loading, error };
}
